import React, { useEffect, useState } from "react";
import { bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux/actions";
import Heading from "pages/_components/Heading";
import FormattedDate from "pages/_components/FormattedDate";
import FormattedAmount from "pages/_components/FormattedAmount";
import TransactionTicket from "pages/_components/TransactionTicket";
import { resizableRoute } from "pages/_components/Resizable";

import {
    actions as multipleTransferActions,
    selectors as multipleTransferSelectors,
} from "reducers/multiple-transfers";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as transactionsSelectors } from "reducers/transactions";
import { selectors as statusSelectors } from "reducers/status";
import { actions as formActions } from "reducers/form";
import { actions as notificationActions } from "reducers/notification";

import * as i18n from "util/i18n";
import { getAccountDescription } from "util/accounts";
import { exportGenericTicketPdfWithSuccessMessage, getGeneralTicketItem } from "util/transaction";
import { actions as globalActions } from "reducers/types/global";
import { multipleTransfersPre } from "middleware/multiple-transfers";
import { VALIDATION_ERROR } from "util/responses";
import { getMultipleTransfersI18nKey } from "util/multiple-transfers";
import { resolveEmbeddedV3GoBackPath } from "util/v3ShellLayoutSync";
import {
    isMasspaymentsCmfApiEnabled,
    MULTIPLE_TRANSFER_VOUCHER_COELSA_ID_LABEL_I18N_KEY,
} from "util/multipleTransfersCmf";
import MultipleTransferTicketData from "./MultipleTransferTicketData";

const FORM_ID = "multiple.transfers";

function MultipleTransferTicket(props) {
    const {
        dispatch,
        fetching,
        isDesktop,
        isSmallDesktop,
        embeddedInV3Shell = false,
        summary: {
            batchId,
            idTransactionStatus,
            debitAccount,
            description,
            creationDate,
            executionDate,
            fileName,
            quantity,
            amount,
        },
        summary,
        snackbarMessage,
        transactionRoute,
        lastHrefPathname,
    } = props;

    const [fetchingDownload, setFetchingDownload] = useState(false);

    const title = getMultipleTransfersI18nKey(`${FORM_ID}.ticket.title`);
    const transactionRouteFallback =
        transactionRoute === "/pendingTransaction/list" || transactionRoute === "/transactions/list"
            ? transactionRoute
            : "/transfer/multiple";
    const backPath = resolveEmbeddedV3GoBackPath(transactionRouteFallback, lastHrefPathname);

    useEffect(() => {
        if (!summary?.idTransaction && localStorage.getItem("flow")) {
            dispatch(push(`${JSON.parse(localStorage.getItem("flow"))}`));
        } else {
            dispatch(multipleTransferActions.setRememberFormData(false));
            if (snackbarMessage && snackbarMessage !== "") {
                dispatch(notificationActions.showNotification(snackbarMessage, "warning", [FORM_ID]));
                dispatch(globalActions.setVoucherSnackbar(null));
            }
        }
        // eslint-disable-next-line
    }, [dispatch, summary]);

    const voucherValue = isMasspaymentsCmfApiEnabled() ? summary?.voucherNumber ?? summary?.voucher_number : undefined;
    const voucherField =
        voucherValue != null && String(voucherValue).trim() !== ""
            ? {
                  voucherNumber: {
                      value: String(voucherValue).trim(),
                      format: "string",
                  },
              }
            : {};

    const additionalData = {
        debitAccount: {
            value: debitAccount?.number !== undefined && getAccountDescription(debitAccount),
            format: "string",
        },
        reference: {
            value: description,
            format: "string",
        },
        uploadDate: {
            value: creationDate,
            format: "datetime",
        },
        executionDate: {
            value: executionDate,
            format: "date",
        },
        fileName: {
            value: fileName,
            format: "string",
        },
        quantity: {
            value: quantity,
            format: "string",
        },
        amount: {
            value: amount?.quantity,
            format: `amount${i18n.get(`currency.label.${amount?.currency || "ARS"}`)}`,
        },
    };

    let data;
    if (batchId) {
        data = {
            id: {
                value: String(batchId),
                format: "string",
            },
            ...voucherField,
            ...additionalData,
        };
    } else if (Object.keys(voucherField).length > 0) {
        data = { ...voucherField, ...additionalData };
    } else {
        data = additionalData;
    }

    const generateTicketBody = () => {
        const cmfApi = isMasspaymentsCmfApiEnabled();
        const formattedData = Object.entries(data).map(([key, item]) => {
            const labelKey =
                cmfApi && key === "voucherNumber"
                    ? MULTIPLE_TRANSFER_VOUCHER_COELSA_ID_LABEL_I18N_KEY
                    : `${FORM_ID}.summary.${key}.label`;
            return getGeneralTicketItem(labelKey, item.value, item.format);
        });

        return formattedData;
    };

    const handleDownload = async () => {
        setFetchingDownload(true);

        const ticketData = {
            ticketName: i18n.get(title),
            ticketBody: generateTicketBody(),
        };

        await exportGenericTicketPdfWithSuccessMessage(ticketData, dispatch, i18n.get("global.download.success"));

        setFetchingDownload(false);
    };

    const centerContentMobile = () => (
        <div className="title-account-header-multiline-background-blue">
            <h1 className="w-100 m-0 px-2">{i18n.get(title)}</h1>
        </div>
    );

    const buttonsDetail = () => {
        const buttons = [
            {
                label: getMultipleTransfersI18nKey(`${FORM_ID}.ticket.goTo`),
                bsStyle: "primary",
                onClick: () => {
                    dispatch(push("/transfer/multiple"));
                },
            },
        ];

        return buttons;
    };

    const signTransaction = () => {
        const { idActivity } = summary;

        const cmfApi = isMasspaymentsCmfApiEnabled();
        const signTransactionData = Object.entries(data).map(([key, item]) => {
            if (key === "reference") {
                return (
                    <div className="transfer-data data-wrapper-flex" key={key}>
                        <span className="data-label no-wrap">{i18n.get(`${FORM_ID}.summary.${key}.label`)}</span>{" "}
                        <span
                            className="data-desc text-right m-0"
                            style={{
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                maxWidth: "100%",
                                flex: "1",
                                textAlign: "right",
                            }}>
                            {item.value}
                        </span>
                    </div>
                );
            }
            const summaryLabelKey =
                cmfApi && key === "voucherNumber"
                    ? MULTIPLE_TRANSFER_VOUCHER_COELSA_ID_LABEL_I18N_KEY
                    : `${FORM_ID}.summary.${key}.label`;
            return (
                <Heading.DataGroup
                    key={key}
                    containerClassName="transfer-data data-wrapper-flex"
                    label={summaryLabelKey}
                    labelClassName="no-wrap"
                    data={(() => {
                        if (item.format.includes("date")) {
                            return (
                                <FormattedDate
                                    date={item.value}
                                    dateFormat={item.format === "datetime" ? "dd/MM/yyyy hh:mm:ss" : "dd/MM/yyyy"}
                                    showTime={item.format === "datetime"}
                                />
                            );
                        }
                        if (item.format.includes("amount")) {
                            return (
                                <FormattedAmount
                                    {...amount}
                                    notBold
                                    className={`${isDesktop ? "f-size-4" : "f-size-65"}`}
                                />
                            );
                        }
                        return item.value;
                    })()}
                    dataClassName="data-desc text-right"
                />
            );
        });

        dispatch(push("/transactions/sign"));
        dispatch(
            formActions.signTransactionData({
                idTransaction: summary?.idTransaction,
                idActivity,
                idForm: "",
                content: signTransactionData,
                title,
                pendingDispatch: summary?.pendingDispatch,
            }),
        );

        dispatch(
            formActions.signTransactionPreview({
                idActivity,
                idTransaction: summary.idTransaction,
                idForm: "",
                ticketData: summary,
            }),
        );
    };

    const validateSignTransaction = async () => {
        dispatch(multipleTransferActions.setFetching(true));
        const { type, data: dataResponse } = await multipleTransfersPre();
        if (type === "W") {
            const { NO_FIELD } = dataResponse.data;
            if (dataResponse.code === VALIDATION_ERROR) {
                dispatch(notificationActions.showNotification(NO_FIELD, "warning", [FORM_ID]));
            }
        } else {
            signTransaction();
        }
        dispatch(multipleTransferActions.setFetching(false));
    };

    const handleBackToMultiple = () => {
        dispatch(push(backPath));
    };

    return (
        <TransactionTicket
            embeddedInV3Shell={embeddedInV3Shell}
            embeddedV3GoBackPath={backPath}
            notificationScope={FORM_ID}
            handleBack={handleBackToMultiple}
            handleClick={handleDownload}
            handleClickMessage="global.download"
            headerClassName={!isDesktop ? "blue-main-header-mobile blue-main-title-mobile" : "px-5"}
            centerElement={!isDesktop ? centerContentMobile : undefined}
            uniqueDownload={["FINISHED"].includes(idTransactionStatus)}
            buttonsDetail={buttonsDetail()}
            hasInlineButtons
            idSubtitle={`${FORM_ID}.ticket.subtitle.${idTransactionStatus?.toLowerCase()}`}
            hideMobileMenu={!isDesktop}
            summary={{ ...summary, idTransactionStatus }}
            isDesktop={isDesktop}
            isSmallDesktop={isSmallDesktop}
            onSignTicket={validateSignTransaction}
            summaryTitle={title}
            showDrawerMobile={false}
            closeMobileDrawer={() => {}}
            rightContent={() => null}
            isFetchingExport={fetchingDownload}
            fetching={fetching}
            ignoreHomeButton
            downloadImageWhite={!isDesktop}>
            <MultipleTransferTicketData
                dispatch={dispatch}
                isDesktop={isDesktop}
                data={data}
                batch={{ ...summary, idUser: summary?.idUserCreator }}
            />
        </TransactionTicket>
    );
}

MultipleTransferTicket.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    isSmallDesktop: bool.isRequired,
    fetching: bool.isRequired,
    summary: shape({}).isRequired,
    loggedUser: shape({}).isRequired,
    snackbarMessage: string.isRequired,
    transactionRoute: string,
    embeddedInV3Shell: bool,
    lastHrefPathname: string,
};

MultipleTransferTicket.defaultProps = {
    embeddedInV3Shell: false,
    transactionRoute: "/desktop",
    lastHrefPathname: undefined,
};

const mapStateToProps = (state) => ({
    fetching: multipleTransferSelectors.getFetching(state),
    summary: multipleTransferSelectors.getMultipleTransfer(state),
    loggedUser: sessionSelectors.getUser(state),
    snackbarMessage: multipleTransferSelectors.getSnackbarMessage(state),
    transactionRoute: transactionsSelectors.getTransactionRoute(state),
    lastHrefPathname: statusSelectors.getPathname(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(MultipleTransferTicket);
